import { Box, TableCell, TableRow } from '@material-ui/core';
import {
  formatDateReadable,
  formatDateTimeReadable,
  formatMoney,
  getPaymentPlanByExternalId,
  patchUpdatePaymentPlanLineSentDate,
  PaymentPlan,
  PaymentPlanLine,
  PaymentPlanLineStatus,
} from '@nemuru-eng/js-api-client';
import { PaymentProvider } from '@nemuru-eng/js-api-client/slim';
import { useState } from 'react';

import { PaymentAttemptsDialog } from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentAttemptsDialog/PaymentAttemptsDialog';
import { PaymentPlanLineStatusTag } from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/components/PaymentPlanLineStatusTag';
import { PaymentPlanLineTableAction } from '@/components/modules/PaymentPlan/components/PaymentPlanTableSection/components/PaymentPlanTable/types/PaymentPlanLineTableAction';
import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { Font } from '@/components/shared/components/Font';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { Text } from '@/components/shared/components/Text';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';
import { useScreen } from '@/hooks/useScreen';
import { amountToValueMoneyMapper } from '@/utils/mappers/amountToValueMoneyMapper';

import { PaymentPlanLineActionsDialog } from './PaymentPlanLineActionsDialog/PaymentPlanLineActionsDialog';

const DIALOG_ACTIONS = [
  PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_DUE_DATE,
  PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_PAYMENT_DATE,
  PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_QUOTA_AMOUNT,
  PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_STATUS,
];

interface PaymentPlanTableRowProps {
  paymentPlan: PaymentPlan;
  paymentPlanLine: PaymentPlanLine;
  editModeAllowed: boolean;
  tableProps: { classes; stickyCellStyle };
}

export const PaymentPlanTableRow = ({
  paymentPlan,
  paymentPlanLine,
  editModeAllowed,
  tableProps,
}: PaymentPlanTableRowProps) => {
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();

  const { isMobile } = useScreen();
  const { opened, setOpened, toggleDialog } = useDialog();
  const { revalidate } = useCache(getPaymentPlanByExternalId, { externalId: paymentPlan.externalId });
  const { request, loading } = useRequest();
  const [action, setAction] = useState<PaymentPlanLineTableAction | undefined>(undefined);
  const { classes, stickyCellStyle } = tableProps;

  const isActionDialog = DIALOG_ACTIONS.some((rowAction) => rowAction === action);
  const allowSendPaymentNotification =
    paymentPlanLine.status === PaymentPlanLineStatus.PENDING && paymentPlan.paymentProvider === PaymentProvider.FLYWIRE;

  const PaymentPlanTableRowActions: ActionsOption[] = [
    {
      display: <Text tId={'SEE_PAYMENT_ATTEMPTS'} />,
      onClick: () => {
        setAction(PaymentPlanLineTableAction.SEE_PAYMENT_ATTEMPTS);
        setOpened(true);
      },
      iconName: 'payments',
      disabled: paymentPlan.paymentProvider !== PaymentProvider.FLYWIRE,
    },
    {
      display: <Text tId={'UPDATE_PAYMENT_PLAN_LINE_STATUS'} />,
      onClick: () => {
        setAction(PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_STATUS);
        setOpened(true);
      },
      iconName: 'currency_exchange',
    },
    {
      display: <Text tId={'UPDATE_PAYMENT_PLAN_LINE_QUOTA_AMOUNT'} />,
      onClick: () => {
        setAction(PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_QUOTA_AMOUNT);
        setOpened(true);
      },
      iconName: 'paid',
      disabled: paymentPlanLine.status !== PaymentPlanLineStatus.PENDING,
    },
    {
      display: <Text tId={'UPDATE_PAYMENT_PLAN_LINE_DUE_DATE'} />,
      onClick: () => {
        setAction(PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_DUE_DATE);
        setOpened(true);
      },
      iconName: 'calendar_month',
      disabled: paymentPlanLine.status !== PaymentPlanLineStatus.PENDING,
    },
    {
      display: <Text tId={'UPDATE_PAYMENT_PLAN_LINE_PAYMENT_DATE'} />,
      onClick: () => {
        setAction(PaymentPlanLineTableAction.UPDATE_PAYMENT_PLAN_LINE_PAYMENT_DATE);
        setOpened(true);
      },
      iconName: 'today',
      disabled: paymentPlanLine.status !== PaymentPlanLineStatus.PAID,
    },
    {
      display: <Text tId={'SEND_PAYMENT_NOTIFICATION'} />,
      onClick: () => {
        setAction(PaymentPlanLineTableAction.SEND_PAYMENT_NOTIFICATION);
        setOpened(true);
      },
      iconName: 'email',
      disabled: !allowSendPaymentNotification,
    },
    {
      display: <Text tId={'COPY_PAYMENT_LINE_ID'} />,
      onClick: () => {
        navigator.clipboard.writeText(paymentPlanLine.id);
        setSnackbarNotification({
          msg: 'ID_COPIED_SUCCESSFULLY',
          level: 'success',
        });
      },
      iconName: 'copy',
    },
  ];

  const handlePaymentNotification = async () => {
    await request(patchUpdatePaymentPlanLineSentDate, { id: paymentPlanLine.id });
    revalidate();
    setAction(undefined);
    setOpened(false);
  };

  return (
    <>
      <TableRow key={paymentPlanLine.id} className={classes.root}>
        <TableCell align={'center'} style={isMobile ? { ...stickyCellStyle, minWidth: '20px', maxWidth: '20px' } : {}}>
          <Font variant={'body2'} color={'textPrimary'}>
            {paymentPlanLine.quotaNumber + 1}
          </Font>
        </TableCell>
        <TableCell align={'left'} width={'140px'}>
          <Font variant={'body2'} color={'textPrimary'}>
            {formatDateReadable(paymentPlanLine.dueDate)}
          </Font>
        </TableCell>
        <TableCell align={'left'} width={'140px'}>
          <Font variant={'body2'} color={'textPrimary'}>
            {paymentPlanLine.sentDate ? formatDateTimeReadable(paymentPlanLine.sentDate) : '-'}
          </Font>
        </TableCell>
        <TableCell align={'left'} width={'140px'}>
          <Font variant={'body2'} color={'textPrimary'}>
            {paymentPlanLine.paymentDate ? formatDateTimeReadable(paymentPlanLine.paymentDate) : '-'}
          </Font>
        </TableCell>
        <TableCell align={'left'} width={'140px'}>
          <Font variant={'body2'} color={'textPrimary'}>
            {formatMoney(amountToValueMoneyMapper(paymentPlanLine.quota))}
          </Font>
        </TableCell>
        <TableCell align={'left'} width={'50px'}>
          <Font variant={'body2'} color={'textPrimary'}>
            <PaymentPlanLineStatusTag paymentPlanLine={paymentPlanLine} />
          </Font>
        </TableCell>
        {
          <TableCell align={'right'} width={'30px'}>
            <MoreActionsMenuWithIcon actionsOptionsArray={editModeAllowed ? PaymentPlanTableRowActions : []} />
          </TableCell>
        }
      </TableRow>
      {action === PaymentPlanLineTableAction.SEND_PAYMENT_NOTIFICATION && (
        <ActionDialog
          Title={<Text tId="PAYMENT_NOTIFICATION" />}
          Description={<Text tId="PAYMENT_NOTIFICATION_TEXT" />}
          MainButton={<Text tId="PAYMENT_NOTIFICATION_BTN" />}
          disabled={loading}
          onSubmit={handlePaymentNotification}
          opened={opened}
          setOpened={setOpened}
          handleClose={toggleDialog}
        >
          <Box mt={4}>
            <ReadOnlyDataRow underlined labelTextId={'QUOTA_NUMBER'} value={paymentPlanLine.quotaNumber + 1} />
            <ReadOnlyDataRow
              underlined
              labelTextId={'AMOUNT'}
              value={formatMoney(amountToValueMoneyMapper(paymentPlanLine.quota))}
            />
            <ReadOnlyDataRow underlined labelTextId={'DUE_DATE'} value={formatDateReadable(paymentPlanLine.dueDate)} />
          </Box>
        </ActionDialog>
      )}
      {action === PaymentPlanLineTableAction.SEE_PAYMENT_ATTEMPTS && opened && (
        <PaymentAttemptsDialog opened={opened} setOpened={setOpened} paymentPlanLine={paymentPlanLine} />
      )}
      {action && isActionDialog && opened && (
        <PaymentPlanLineActionsDialog
          changePaymentAction={action}
          opened={opened}
          setOpened={setOpened}
          toggleDialog={toggleDialog}
          paymentPlanLine={paymentPlanLine}
          paymentPlan={paymentPlan}
        />
      )}
    </>
  );
};
